import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { Row, Col, Card, CardBody, Input, Button, Table } from "reactstrap";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const DispatchAnalytics = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState(null);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        date_from: "2026-02-01",
        date_to: "2026-02-28",
        status: "",
        search: "",
    });

    const limit = 20;

    const loadReports = async () => {
        dispatch(loadingTrue());
        let url = `/reports/dispatch_report.php?page=${page}&limit=${limit}`;
        if (filters.date_from) url += `&date_from=${filters.date_from}`;
        if (filters.date_to) url += `&date_to=${filters.date_to}`;
        if (filters.status) url += `&status=${filters.status}`;
        if (filters.search) url += `&search=${filters.search}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setReportData(res);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => {
        loadReports();
    }, [page, filters.date_from, filters.date_to]);

    if (!reportData) return null;

    const summary = reportData?.summary_cards;
    const statusChart = reportData?.status_chart;
    const dispatchList = reportData?.dispatch_list || [];
    const pagination = reportData?.pagination;

    return (
        <div className="p-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold text-primary">Dispatch Analytics Dashboard</h4>
                <div className="d-flex gap-2">
                    <Input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                        className="shadow-sm border-0"
                    />
                    <Input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                        className="shadow-sm border-0"
                    />
                    <Button color="primary" onClick={() => { setPage(1); loadReports(); }}>
                        <i className="bi bi-filter"></i>
                    </Button>
                </div>
            </div>

            {/* KPI SUMMARY SECTION */}
            <Row className="mb-4 g-3">
                {[
                    { label: "Total Dispatch", val: summary?.total_dispatch, color: "primary", icon: "box-seam" },
                    { label: "Invoiced", val: summary?.invoiced, color: "info", icon: "receipt" },
                    { label: "Completed", val: summary?.completed, color: "success", icon: "check-circle" },
                    { label: "Assigned", val: summary?.assigned, color: "warning", icon: "truck" },
                ].map((item, i) => (
                    <Col md={3} key={i}>
                        <Card className="border-0 shadow-sm h-100 position-relative overflow-hidden">
                            <CardBody>
                                <div className="text-muted small fw-bold text-uppercase">{item.label}</div>
                                <h2 className="fw-bold mt-2 mb-0">{item.val || 0}</h2>
                                <i className={`bi bi-${item.icon} position-absolute bottom-0 end-0 me-3 mb-2 opacity-25 display-5 text-${item.color}`}></i>
                            </CardBody>
                            <div className={`bg-${item.color}`} style={{ height: '4px' }}></div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="g-4">
                {/* DOUGHNUT STATUS CHART */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100">
                        <CardBody>
                            <h6 className="fw-bold mb-4">Orders by Status</h6>
                            <div style={{ height: "280px" }}>
                                <Doughnut
                                    data={{
                                        labels: statusChart?.labels || [],
                                        datasets: [{
                                            data: statusChart?.datasets?.[0]?.data || [],
                                            backgroundColor: ["#ffc107", "#0d6efd", "#198754", "#0dcaf0"],
                                            hoverOffset: 10,
                                            borderWidth: 0
                                        }]
                                    }}
                                    options={{
                                        maintainAspectRatio: false,
                                        cutout: '75%',
                                        plugins: {
                                            legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20 } }
                                        }
                                    }}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* DISPATCH DATA TABLE */}
                <Col md={8}>
                    <Card className="border-0 shadow-sm h-100">
                        <CardBody>
                            <h6 className="fw-bold mb-3">Live Dispatch Feed</h6>
                            <Table responsive hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th className="small">Dispatch #</th>
                                        <th className="small">Customer</th>
                                        <th className="small">Route</th>
                                        <th className="small text-center">Pallets</th>
                                        <th className="small text-center">Hours</th>
                                        <th className="small text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody style={{ fontSize: '13px' }}>
                                    {dispatchList.length > 0 ? (
                                        dispatchList.map((row) => (
                                            <tr key={row.id}>
                                                <td className="fw-bold text-primary">{row.dispatch_number}</td>
                                                <td>
                                                    <div className="fw-semibold">{row.company_name}</div>
                                                    <small className="text-muted">{row.vessel_name}</small>
                                                </td>
                                                <td>
                                                    <div className="text-truncate" style={{ maxWidth: '180px' }}>
                                                        <i className="bi bi-geo-alt-fill text-danger me-1"></i>
                                                        {row.discharge_point} → {row.end_point}
                                                    </div>
                                                </td>
                                                <td className="text-center">{row.pallet_count}</td>
                                                <td className="text-center fw-bold">{parseFloat(row.total_hours).toFixed(1)}h</td>
                                                <td className="text-center">
                                                    <span className={`status-badge ${row.status}`}>{row.status}</span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="6" className="text-center py-4">No data found in this range</td></tr>
                                    )}
                                </tbody>
                            </Table>
                            <div className="mt-3">
                                <Pagination
                                    page={page}
                                    total={pagination?.total_rows || 0}
                                    limit={limit}
                                    onPrev={() => setPage((p) => Math.max(1, p - 1))}
                                    onNext={() => setPage((p) => p + 1)}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DispatchAnalytics;